import { CommonModule, DOCUMENT } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';

@Component({
  selector: 'app-confirm-modal',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './confirm-modal.component.html',
  styleUrls: ['./confirm-modal.component.scss'],
})
export class ConfirmModalComponent implements OnChanges, OnDestroy {
  /** Controls visibility. The parent owns this value. */
  @Input() open = false;
  @Input() title = 'Are you sure?';
  @Input() message = '';
  @Input() confirmLabel = 'Confirm';
  @Input() cancelLabel = 'Cancel';
  /** Styles the confirm button red for delete-type actions. */
  @Input() destructive = false;

  @Output() confirmed = new EventEmitter<void>();
  /** Fires on Cancel, Esc, or a click on the backdrop. */
  @Output() cancelled = new EventEmitter<void>();

  @ViewChild('dialog') private dialog?: ElementRef<HTMLElement>;
  @ViewChild('cancelBtn') private cancelBtn?: ElementRef<HTMLButtonElement>;

  readonly titleId = `confirm-modal-title-${Math.random().toString(36).slice(2, 8)}`;
  readonly messageId = `confirm-modal-msg-${Math.random().toString(36).slice(2, 8)}`;

  private previouslyFocused: HTMLElement | null = null;

  constructor(@Inject(DOCUMENT) private doc: Document) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['open']) return;

    if (this.open) {
      this.previouslyFocused = this.doc.activeElement as HTMLElement | null;
      this.doc.body.style.overflow = 'hidden';
      // Wait for *ngIf to render the dialog, then focus the safe option.
      setTimeout(() => this.cancelBtn?.nativeElement.focus());
    } else {
      this.releaseFocusAndScroll();
    }
  }

  ngOnDestroy(): void {
    this.releaseFocusAndScroll();
  }

  onConfirm(): void {
    this.confirmed.emit();
  }

  onCancel(): void {
    this.cancelled.emit();
  }

  onBackdropClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) this.onCancel();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.open) this.onCancel();
  }

  /** Keeps Tab / Shift+Tab inside the dialog while it is open. */
  @HostListener('document:keydown', ['$event'])
  trapFocus(event: KeyboardEvent): void {
    if (!this.open || event.key !== 'Tab' || !this.dialog) return;

    const focusable = Array.from(
      this.dialog.nativeElement.querySelectorAll<HTMLElement>('button:not([disabled])')
    );
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = this.doc.activeElement;

    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  private releaseFocusAndScroll(): void {
    this.doc.body.style.overflow = '';
    this.previouslyFocused?.focus?.();
    this.previouslyFocused = null;
  }
}